import React, { useMemo } from "react"

import { useSearchParams } from "react-router-dom"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types.ts"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"
import type { ISelectRawOption } from "@front-shared/types/objects.types.ts"

import { GENERAL_CONFIG } from "../../../config/general.config.ts"
import { POLICIES_CONFIG } from "../../../config/policies.config"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { organizationSelector } from "../../../store/selectors/organization.selector"

import closeIcon from "@front-shared/assets/images/icons/close-input-control.svg"

export const PoliciesFiltersDisplayComponent: React.FunctionComponent<
    IPoliciesFiltersDisplayComponentProps
> = props => {
    const [, setSearchParams] = useSearchParams()

    const organizationState = useAppSelector(organizationSelector)

    const mappedFilters = useMemo<ISelectRawOption[]>(() => {
        if (!props.listType) {
            return []
        }

        return PoliciesListHelper.getMappedDisplayFilters(
            props.filters,
            props.listType,
            organizationState.users,
        )
    }, [props.filters, props.listType, organizationState.users])

    const onRemoveFilter = (key: keyof IPoliciesListFiltersData) => {
        const newFilters = { ...props.filters }
        delete newFilters[key]

        BrowserStorageHelper.set(
            props.listType +
                GENERAL_CONFIG.browserStorageKeys
                    .policiesFiltersConfigurationSuffix,
            newFilters,
        )

        setSearchParams({
            [GENERAL_CONFIG.urlSearchParamsKeys.filters]:
                JSON.stringify(newFilters),
        })
    }

    return mappedFilters.length ? (
        <div
            className={`
                relative flex items-start
                ${props.className}
            `}
        >
            <PrimaryText
                className="mr-[8px] mt-[3px] flex-shrink-0"
                typography="h6"
            >
                {props.prefixLabel || "Filtering by:"}
            </PrimaryText>

            <div className="flex items-center flex-wrap gap-2">
                {mappedFilters.map((item, index) => {
                    const filterKey = item.key as keyof IPoliciesListFiltersData

                    const hideRemoveFilterButton =
                        ["checkDate", "responseType"].includes(filterKey) ||
                        !props.listType ||
                        props.hideRemoveFilterButtons

                    return (
                        <div
                            key={index}
                            className="rounded-[22px] bg-brand-warmLight flex justify-between"
                        >
                            <PrimaryText
                                className={`
                                    pl-[16px] pt-[8px] pb-[6px]
                                    ${hideRemoveFilterButton ? "pr-[16px]" : ""}
                                    ${
                                        item.key &&
                                        POLICIES_CONFIG.filtersMouseFlowIgnoreFields.includes(
                                            item.key as keyof IPoliciesListFiltersData,
                                        )
                                            ? "mouseflow-ignore"
                                            : ""
                                    }
                                `}
                                size={14}
                                lineHeight={14}
                            >
                                {item.label}: {item.value}
                            </PrimaryText>

                            {!hideRemoveFilterButton && (
                                <button
                                    className="cursor-pointer pl-[12px] pr-[16px] rounded-r-[22px]"
                                    type="button"
                                    onClick={() => {
                                        onRemoveFilter(filterKey)
                                    }}
                                >
                                    <img src={closeIcon} alt="Remove" />
                                </button>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    ) : null
}

interface IPoliciesFiltersDisplayComponentProps {
    listType?: PoliciesListType
    className?: string
    prefixLabel?: string
    filters: IPoliciesListFiltersData
    hideRemoveFilterButtons?: boolean
}
